import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Mail, ArrowRight } from "lucide-react";
import { useScrollAnimation } from "@/hooks/use-scroll-animation";
import { useLanguage } from "@/contexts/LanguageContext";
import ctaImage from "@/assets/cta-banner.jpg";
import Lottie from "lottie-react";
import basketballAnimation from "@/assets/basketball-player.json";

export const CTABanner = () => {
  const { visibleItems, itemRefs } = useScrollAnimation(0.2);
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden my-24 px-6">
      <div className="max-w-[1300px] mx-auto">
        <div className="relative rounded-3xl overflow-hidden shadow-hover-strong border-2 border-blob/30 group">
          {/* Background Image with Overlay */}
          {/* <div className="absolute inset-0 z-0">
            <img 
              src={ctaImage} 
              alt="Contact banner background" 
              className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500"
            />
            <div className="absolute inset-0 z-0 bg-gradient-to-r from-background via-background/95 to-background/90" />
          </div> */}

          {/* Animated gradient blobs */}
          <div className="absolute inset-0 opacity-20 z-0">
            <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blob/60 to-transparent rounded-full blur-3xl animate-blob" />
            <div className="absolute bottom-0 right-0 w-80 h-80 bg-gradient-to-tl from-blob/50 to-transparent rounded-full blur-3xl animate-blob animation-delay-3000" />
          </div>

          {/* Content */}
          <div className="relative z-10 px-8 md:px-16 py-16 md:py-20">
            <div className="max-w-3xl relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-blob/10 border border-blob/30 mb-6 animate-fade-in">
                <span className="w-2 h-2 rounded-full bg-blob animate-pulse flex-shrink-0" />
                <span className="text-sm font-medium text-readable-accent flex items-center">{t("cta.tag")}</span>
              </div>

              <h2 className="text-4xl md:text-5xl lg:text-6xl font-normal mb-6 text-foreground animate-fade-in relative z-10 leading-[1.05] tracking-tight">
                {t("cta.title")}
              </h2>
              
              <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-2xl animate-fade-in-slow relative z-10">
                {t("cta.subtitle")}
              </p>

              <div className="flex flex-col md:flex-row gap-4 animate-fade-in-slow relative z-10">
                <Button 
                  size="lg" 
                  variant="premium"
                  className="w-full md:w-auto group"
                  asChild
                >
                  <Link to="/contact">
                    <Mail className="w-5 h-5 mr-2" />
                    {t("cta.button")}
                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </Button>
              </div>

              {/* Decorative elements */}
              <div className="flex flex-col items-start gap-3 mt-12 opacity-60 relative z-10" style={{ transform: 'none' }}>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground font-medium ml-0" style={{ transform: 'none' }}>{t("cta.years")}</span>
                  <div className="w-12 h-0.5 bg-gradient-to-r from-blob to-transparent" />
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm text-muted-foreground font-medium ml-0" style={{ transform: 'none' }}>{t("cta.aiDesign")}</span>
                  <div className="w-12 h-0.5 bg-gradient-to-r from-blob to-transparent" />
                </div>
              </div>
            </div>
          </div>

          {/* Lottie Animation */}
          <div 
            ref={(el) => (itemRefs.current[0] = el)}
            className={`absolute -right-24 top-8 md:-right-80 md:top-1/2 md:-translate-y-1/2 w-[300px] h-[300px] md:w-[768px] md:h-[768px] lg:w-[960px] lg:h-[960px] z-50 mix-blend-normal pointer-events-none transition-all duration-1000 ${
              visibleItems.has(0) ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'
            }`}
          >
            <Lottie animationData={basketballAnimation} loop={true} />
          </div>

          {/* Corner decorations */}
          <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl from-blob/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blob/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-0" />
        </div>
      </div>
    </section>
  );
};
